// Audio-Einrichtung: zwei Wege ins Studio.
//  1. Dein Interface im Browser: jedes normale Audio-Interface oder Mikrofon, ohne Installation (Eingang 1, 2 oder Stereo).
//  2. SRS Bridge: Dante (Digiface Dante, Dante Virtual Soundcard) und alle weiteren Kanäle, verlustfrei lokal, eigene Plugins.
//     Weg: Dante-Geräte → Dante Controller → Core Audio / ASIO → SRS Bridge → Browser.
import { useEffect, useState } from 'react';
import type { Track } from '@srs/shared';
import { bridge, type BridgeStatus } from '../bridge/client.ts';
import { midiIn } from '../audio/midiin.ts';
import { useStudio } from '../state/store.ts';
import { Meter, Panel, toast } from '../components/ui.tsx';
import { apiBase } from '../api/client.ts';
import { browserLatency, closeInput, inputInfo, inputLabel, inputLevel, inputOpen, inputPrefs, listInputs, measureBrowserLatency, onInputChange, openInput, setInputPrefs } from '../audio/recorder.ts';

/** Status der Bridge. Pegel kommen bis 30-mal je Sekunde; ohne withMeters zeichnet die Komponente dafür nicht neu. */
export function useBridge(withMeters = false) {
  const [s, setS] = useState<BridgeStatus>(bridge.status);
  useEffect(() => {
    let last = bridge.status;
    const off = bridge.onStatus((n) => {
      const onlyMeters = !withMeters && (Object.keys(n) as (keyof BridgeStatus)[]).every((k) => k === 'meters' || k === 'peaks' || n[k] === last[k]);
      last = n;
      if (!onlyMeters) setS(n);
    });
    return () => { off(); };
  }, [withMeters]);
  return s;
}

/** Eingang einer Spur wählen: In 1, In 2, In 1+2 (Browser) oder ein Kanal der Bridge */
export function InputSelect({ t, small }: { t: Track; small?: boolean }) {
  const commit = useStudio((x) => x.commit);
  const s = useBridge();
  const value = t.input?.kind === 'bridge' ? `b:${t.input.channel}` : t.input?.kind === 'browser' && t.input.stereo ? `s:${t.input.ch ?? 0}` : `m:${t.input?.kind === 'browser' ? t.input.ch ?? 0 : 0}`;
  const set = (v: string) => commit('Eingang wählen', (p) => {
    const x = p.tracks.find((y) => y.id === t.id); if (!x) return;
    const [k, n] = v.split(':'), ch = Number(n);
    if (k === 'b') x.input = { kind: 'bridge', channel: ch, label: s.device?.inputs.find((c) => c.channel === ch)?.name };
    else x.input = { kind: 'browser', ch, stereo: k === 's' || undefined };
  });
  const bridgeIns = s.connected ? s.device?.inputs ?? [] : [];
  return (
    <select className={`input insel ${small ? 'small' : ''}`} value={value} title="Eingang dieser Spur" onClick={(e) => e.stopPropagation()} onChange={(e) => set(e.target.value)}>
      <optgroup label="Dein Interface (Browser)">
        <option value="m:0">In 1</option>
        <option value="m:1">In 2</option>
        <option value="s:0">In 1+2 (Stereo)</option>
      </optgroup>
      {bridgeIns.length > 0 && <optgroup label={`Bridge · ${s.device?.name ?? ''}`}>{bridgeIns.map((c) => <option key={c.channel} value={`b:${c.channel}`}>{c.channel + 1}: {c.name}</option>)}</optgroup>}
      {t.input?.kind === 'bridge' && !bridgeIns.length && <option value={value}>{inputLabel(t)} (Bridge nicht verbunden)</option>}
    </select>
  );
}

/** Pegel des Browser-Eingangs je Kanal (laufend aktualisiert) */
function useInputLevels() {
  const [lv, setLv] = useState<number[]>([0, 0]);
  useEffect(() => {
    let raf = 0, hold = [0, 0];
    const loop = () => { hold = [0, 1].map((i) => Math.max(inputLevel.peaks[i] ?? 0, (hold[i] ?? 0) * 0.92)); setLv(hold); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return lv;
}

function BrowserInput() {
  const tracks = useStudio((x) => x.project.tracks);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [, force] = useState(0);
  const [busy, setBusy] = useState(false);
  const levels = useInputLevels();
  const open = inputOpen();
  const prefs = inputPrefs();
  const lat = browserLatency();
  useEffect(() => onInputChange(() => force((n) => n + 1)), []);
  const refresh = async () => setDevices(await listInputs());
  useEffect(() => { void refresh(); navigator.mediaDevices?.addEventListener?.('devicechange', refresh); return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh); }, []);
  const start = async (id?: string) => {
    try { await openInput(id); await refresh(); force((n) => n + 1); } catch (e) { toast(`Kein Zugriff auf das Interface: ${(e as Error).message}. Im Browser das Mikrofon für diese Seite erlauben.`); }
  };
  const measure = async () => {
    setBusy(true);
    try { const r = await measureBrowserLatency(); toast(`Latenz gemessen: ${String(r.ms).replace('.', ',')} ms (Signalgüte ${Math.round(r.quality * 100)} %)`); } catch (e) { toast((e as Error).message); }
    setBusy(false); force((n) => n + 1);
  };
  const db = (v: number) => (v > 0.00002 ? `${(20 * Math.log10(v)).toFixed(1).replace('.', ',')} dB` : '−∞');
  const audio = tracks.filter((t) => t.kind === 'audio');
  const labelsKnown = devices.some((d) => d.label);
  return (
    <Panel title="Dein Interface" right={<span className={`pill ${open ? 'green' : ''}`}><i className="ldot" />{open ? 'Eingang offen' : 'ohne Installation'}</span>}>
      <div className="body stack">
        <div className="t2">Jedes normale Audio-Interface (Focusrite, Universal Audio, RME, Steinberg, Audient, MOTU …) oder ein USB-Mikrofon. Aufgenommen wird verlustfrei mit 24 Bit, Eingang 1, 2 oder beide.</div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {!labelsKnown || !open
            ? <button className="btn primary" onClick={() => void start(prefs.deviceId)}>{open ? 'Eingang neu öffnen' : 'Interface verbinden'}</button>
            : null}
          {labelsKnown && (
            <select className="input" style={{ minWidth: 260 }} value={devices.find((d) => d.label === inputInfo.label)?.deviceId ?? prefs.deviceId ?? ''} onChange={(e) => void start(e.target.value)}>
              {devices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Eingang'}</option>)}
            </select>
          )}
          {open && <button className="btn small" onClick={() => { closeInput(); force((n) => n + 1); }}>Schließen</button>}
        </div>
        {open && <>
          <div className="inmeters">
            {[0, 1].slice(0, Math.max(1, inputInfo.channels)).map((c) => (
              <div key={c} className="inm"><b>In {c + 1}</b><div className="bar"><i style={{ width: `${Math.min(100, Math.max(0, (20 * Math.log10(Math.max(levels[c], 1e-5)) + 60) / 60 * 100))}%`, background: levels[c] > 0.89 ? 'var(--claim)' : undefined }} /></div><span className="mono">{db(levels[c])}</span></div>
            ))}
          </div>
          <div className="mono t2" style={{ fontSize: 11.5 }}>{inputInfo.label} · {inputInfo.channels} {inputInfo.channels === 1 ? 'Kanal' : 'Kanäle'} · {inputInfo.sampleRate / 1000} kHz{inputInfo.inputLatencyMs ? ` · Eingang meldet ${String(inputInfo.inputLatencyMs).replace('.', ',')} ms` : ''}</div>
          <div className="card stack" style={{ gap: 8 }}>
            <div className="row"><b>Latenzausgleich</b><span className={`pill ${lat.measured ? 'green' : ''}`} style={{ marginLeft: 'auto' }}>{String(Math.round(lat.sec * 10000) / 10).replace('.', ',')} ms {lat.measured ? 'gemessen' : 'geschätzt'}</span></div>
            <div className="t2" style={{ fontSize: 12 }}>Damit Takes genau auf dem Beat liegen. Zum Messen einen Ausgang des Interfaces per Kabel mit einem Eingang verbinden (oder das Mikrofon vor die Boxen halten), Kopfhörer ab, dann messen.</div>
            <div className="row">
              <button className="btn small primary" disabled={busy} onClick={() => void measure()}>{busy ? 'Messe …' : 'Latenz messen'}</button>
              <label className="etl" title="Feinkorrektur von Hand. Positiv: Aufnahme rückt früher.">Feinkorrektur<input className="input" type="number" step={0.5} style={{ width: 70 }} value={prefs.trimMs ?? 0} onChange={(e) => { setInputPrefs({ trimMs: Number(e.target.value) || 0 }); force((n) => n + 1); }} /> ms</label>
              {lat.measured && <button className="lnk" onClick={() => { const m = { ...(prefs.measured ?? {}) }; delete m[inputInfo.label]; setInputPrefs({ measured: m }); force((n) => n + 1); }}>Messung verwerfen</button>}
            </div>
          </div>
        </>}
        <div className="sect" style={{ padding: 0 }}>Eingänge der Spuren</div>
        <table className="table">
          <tbody>
            {audio.map((t) => <tr key={t.id}><td><i className="tdot" style={{ background: t.color }} />{t.name}</td><td style={{ width: 220 }}><InputSelect t={t} /></td><td className="t2" style={{ fontSize: 11.5 }}>{t.armed ? 'scharf' : ''}</td></tr>)}
            {!audio.length && <tr><td className="t2">Noch keine Audiospur. In der Session „+ Audio“.</td></tr>}
          </tbody>
        </table>
        <div className="t2" style={{ fontSize: 12, lineHeight: 1.5 }}>
          <b>Tipps:</b> Abhören über das Direct Monitoring des Interfaces (ohne Verzögerung), Kopfhörer statt Boxen, Interface auf 48 kHz.
          Chrome oder Edge empfohlen. Mehr als zwei Eingänge oder Dante: SRS Bridge rechts.
        </div>
      </div>
    </Panel>
  );
}

/** MIDI-Keyboards über Web MIDI: Geräte, Aktivität, Thru auf die scharf geschaltete MIDI-Spur */
function MidiPanel() {
  const [, force] = useState(0);
  const tracks = useStudio((x) => x.project.tracks);
  const commit = useStudio((x) => x.commit);
  useEffect(() => midiIn.onChange(() => force((n) => n + 1)), []);
  useEffect(() => { const id = setInterval(() => force((n) => n + 1), 250); return () => clearInterval(id); }, []);
  const midi = tracks.filter((t) => t.kind === 'midi');
  const now = performance.now();
  const name = (m: number) => `${['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'H'][m % 12]}${Math.floor(m / 12) - 1}`;
  return (
    <Panel title="MIDI-Keyboard" right={<span className={`pill ${midiIn.devices.length ? 'green' : ''}`}><i className="ldot" />{midiIn.access ? `${midiIn.devices.length} ${midiIn.devices.length === 1 ? 'Gerät' : 'Geräte'}` : 'aus'}</span>}>
      <div className="body stack">
        <div className="t2">Keyboard oder Pad-Controller per USB anschließen. Gespielt wird über das Instrument der scharf geschalteten MIDI-Spur, aufgenommen mit der Aufnahme-Taste wie Audio. Sustain-Pedal wird übernommen.</div>
        {midiIn.error && <div className="card" style={{ borderColor: '#f2c2bc', background: 'var(--claim-wash)', fontSize: 12.5 }}>{midiIn.error}</div>}
        {!midiIn.access
          ? <div><button className="btn primary" disabled={!midiIn.supported} onClick={() => void midiIn.enable()}>MIDI aktivieren</button></div>
          : <>
            <table className="table"><tbody>
              {midiIn.devices.map((d) => {
                const off = midiIn.prefs.disabled.includes(d.id), act = now - (midiIn.activity.get(d.id) ?? -1e9) < 300;
                return <tr key={d.id}>
                  <td><i className={`mact ${act ? 'on' : ''}`} />{d.name}<small className="t2"> {d.manufacturer}</small></td>
                  <td style={{ width: 90 }}><label className="row" style={{ gap: 4 }}><input type="checkbox" style={{ width: 'auto' }} checked={!off} onChange={() => midiIn.setPrefs({ disabled: off ? midiIn.prefs.disabled.filter((x) => x !== d.id) : [...midiIn.prefs.disabled, d.id] })} />aktiv</label></td>
                </tr>;
              })}
              {!midiIn.devices.length && <tr><td className="t2">Kein MIDI-Gerät gefunden. Anschließen, es erscheint automatisch.</td></tr>}
            </tbody></table>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <label className="row" style={{ gap: 6 }}><input type="checkbox" style={{ width: 'auto' }} checked={midiIn.prefs.thru} onChange={(e) => midiIn.setPrefs({ thru: e.target.checked })} />MIDI Thru (live hören)</label>
              {midiIn.lastNote && <span className="pill mono" style={{ marginLeft: 'auto' }}>{name(midiIn.lastNote.m)} · Velocity {Math.round(midiIn.lastNote.v * 127)}</span>}
            </div>
          </>}
        <div className="sect" style={{ padding: 0 }}>MIDI-Spuren</div>
        <table className="table"><tbody>
          {midi.map((t) => <tr key={t.id}><td><i className="tdot" style={{ background: t.color }} />{t.name}</td><td className="t2" style={{ fontSize: 11.5 }}>{t.instrument?.plugin ?? 'Instrument'}</td>
            <td style={{ width: 120 }}><button className={`btn small ${t.armed ? 'danger' : ''}`} onClick={() => { commit('Scharf schalten', (p) => { const x = p.tracks.find((y) => y.id === t.id)!; x.armed = !x.armed; }); void midiIn.enable(); }}>{t.armed ? '● scharf' : 'scharf schalten'}</button></td></tr>)}
          {!midi.length && <tr><td className="t2">Noch keine MIDI-Spur. In der Session „+ MIDI“.</td></tr>}
        </tbody></table>
      </div>
    </Panel>
  );
}

/** Installer der Bridge vom Studio-Server (Mac und Windows), mit Version und Größe */
type Dl = { file: string; version: string; size: number };
function BridgeDownloads() {
  const [d, setD] = useState<{ mac?: Dl; windows?: Dl } | null>(null);
  useEffect(() => { fetch(`${apiBase()}/downloads`).then((r) => (r.ok ? r.json() : {})).then(setD).catch(() => setD({})); }, []);
  if (!d) return null;
  const mb = (n: number) => `${(n / 1048576).toFixed(0)} MB`;
  const isMac = /Mac/.test(navigator.platform), isWin = /Win/.test(navigator.platform);
  const btn = (x: Dl | undefined, label: string, primary: boolean) => x
    ? <a className={`btn ${primary ? 'primary' : ''}`} href={`${apiBase()}/downloads/${x.file}`} download>{label}<span className="mono" style={{ fontSize: 10.5, opacity: 0.8 }}>v{x.version} · {mb(x.size)}</span></a>
    : null;
  if (!d.mac && !d.windows) return <div className="t2" style={{ fontSize: 12 }}>Installer noch nicht hinterlegt. Bitte beim Studio anfragen.</div>;
  return (
    <div className="row" style={{ flexWrap: 'wrap' }}>
      {btn(d.mac, 'Für Mac laden', isMac || !isWin)}
      {btn(d.windows, 'Für Windows laden', isWin)}
    </div>
  );
}

/** Latenzausgleich der Bridge: gemessener Roundtrip und was davon der Aufnahme abgezogen wird */
function LatencyInfo({ s }: { s: BridgeStatus }) {
  const d = s.device!, sr = d.sampleRate || 48000, ms = (n: number) => String(Math.round((n / sr) * 10000) / 10).replace('.', ',');
  const rt = d.measuredRoundTripSamples ?? 0, raw = d.inputLatencySamples + (d.extraLatencySamples ?? 0), comp = rt ? Math.min(raw, rt) : raw;
  return (
    <div className="card row" style={{ marginBottom: 10, gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
      <b>Latenzausgleich</b>
      {rt ? <span className="pill green">Roundtrip {ms(rt)} ms gemessen</span> : <span className="pill">noch nicht gemessen</span>}
      <span className="t2">Aufnahmen werden um <b>{ms(comp)} ms</b> vorgezogen{rt ? '' : ' (nur vom Treiber gemeldet)'}.</span>
      {!rt && <span className="t2" style={{ fontSize: 11.5 }}>Zum Messen Ausgang 1 per Kabel auf Eingang 1 (bei Dante im Dante Controller zurückrouten).</span>}
    </div>
  );
}

export function Dante() {
  const s = useBridge(true);
  const [code, setCode] = useState('');
  const tracks = useStudio((x) => x.project.tracks);
  const commit = useStudio((x) => x.commit);
  const [rate, setRate] = useState(48000);
  const [bufSize, setBufSize] = useState(128);
  const channels = s.device?.inputs ?? [];

  useEffect(() => {
    const off = bridge.on((m) => {
      if (m.type === 'latency') toast(`Latenz gemessen: Roundtrip ${m.roundTripMs.toFixed(1).replace('.', ',')} ms. Aufnahmen werden ab jetzt entsprechend ausgeglichen.`);
      if (m.type === 'error' && m.code === 'latency_error') toast(`Latenzmessung: ${m.message}`);
    });
    return () => { off(); };
  }, []);
  useEffect(() => { if (s.connected && channels.length) bridge.send({ type: 'subscribeMeters', channels: channels.map((c) => c.channel), fps: 20 }); }, [s.connected, channels.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="audio-setup">
      <div className="audio-col"><BrowserInput /><MidiPanel /></div>
      <Panel title="SRS Bridge" right={<span className={`pill ${s.connected ? 'green' : ''}`}><i className="ldot" />{s.connected ? `verbunden · ${s.version ?? ''} · ${s.os ?? ''}` : 'nicht verbunden'}</span>}>
        <div className="body">
          {!s.connected ? (
            <div className="stack" style={{ maxWidth: 600 }}>
              <div className="t2">Für <b>Dante</b> (RME Digiface Dante, Dante Virtual Soundcard) und <b>alle Kanäle</b> eines Interfaces: Die Bridge ist eine kleine App für Mac und Windows. Sie nimmt auf dem Rechner verlustfrei auf, gleicht die Latenz aus, lädt die Takes hoch und bindet eure eigenen Plugins ein. Sie funktioniert mit jedem Interface, das der Rechner kennt (Core Audio, ASIO, WASAPI).</div>
              <BridgeDownloads />
              <ol className="t2" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6, fontSize: 12.5 }}>
                <li>Installer öffnen und durchklicken (Mac und Windows). Die Bridge startet danach von selbst. Meldet der Mac beim Öffnen „nicht verifizierter Entwickler“: Rechtsklick auf den Installer → Öffnen.</li>
                <li>Das Symbol der Bridge erscheint in der Menüleiste (Mac) bzw. im Infobereich (Windows), dort steht der Kopplungscode.</li>
                <li>Code hier eingeben und verbinden. Fragt der Browser nach Zugriff auf „Apps auf diesem Gerät“, bitte zulassen.</li>
                <li>Gerät wählen, Kanäle den Spuren zuweisen, einmal die Latenz messen.</li>
              </ol>
              <div className="row"><input className="input" placeholder="Kopplungscode" value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter') bridge.connect(code || undefined); }} /><button className="btn primary" onClick={() => bridge.connect(code || undefined)}>Verbinden</button></div>
              {s.error && <div className="card" style={{ borderColor: '#f2c2bc', background: 'var(--claim-wash)', fontSize: 12.5 }}>{s.error}</div>}
            </div>
          ) : (
            <>
              {!s.paired && <div className="row" style={{ marginBottom: 10 }}><input className="input" placeholder="Kopplungscode aus der Menüleiste" value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))} /><button className="btn primary" onClick={() => bridge.connect(code || undefined)}>Koppeln</button>{s.error && <span className="pill red">{s.error}</span>}</div>}
              <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                <select className="input" value={s.device?.deviceId ?? ''} onChange={(e) => bridge.send({ type: 'openDevice', deviceId: e.target.value, sampleRate: rate, bufferSize: bufSize })}>
                  <option value="">Gerät wählen …</option>
                  {s.devices.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.inputs} In / {d.outputs} Out, {d.type})</option>)}
                </select>
                <select className="input" value={rate} onChange={(e) => setRate(Number(e.target.value))}>{[44100, 48000, 96000].map((r) => <option key={r} value={r}>{r / 1000} kHz</option>)}</select>
                <select className="input" value={bufSize} onChange={(e) => setBufSize(Number(e.target.value))}>{[64, 128, 256, 512].map((r) => <option key={r} value={r}>{r} Samples</option>)}</select>
                <button className="btn" disabled={!s.device || s.measuring} title="Ausgang 1 per Kabel mit Eingang 1 verbinden (bei Dante: im Dante Controller zurückrouten)" onClick={() => bridge.measureLatency(0, 0)}>{s.measuring ? 'Messe …' : 'Latenz messen (Ausgang 1 → Eingang 1)'}</button>
              </div>
              {s.device && <LatencyInfo s={s} />}
              {s.error && <div className="card row" style={{ borderColor: '#f2c2bc', background: 'var(--claim-wash)', fontSize: 12.5, marginBottom: 10 }}><span>{s.error}</span><button className="lnk" style={{ marginLeft: 'auto' }} onClick={() => bridge.clearError()}>✕</button></div>}
              {!channels.length ? <div className="empty">Noch kein Gerät geöffnet.</div> : (
                <table className="table">
                  <thead><tr><th>Kanal</th><th>Name (bei Dante aus dem Dante Controller)</th><th>Pegel</th><th>Auf Spur</th></tr></thead>
                  <tbody>
                    {channels.map((c) => (
                      <tr key={c.channel}>
                        <td className="mono">{c.channel + 1}</td>
                        <td>{c.name}</td>
                        <td style={{ width: 160 }}><div style={{ transform: 'rotate(90deg)', transformOrigin: 'left top', marginLeft: 12, height: 8 }}><Meter level={s.meters[c.channel] ?? 0} height={120} /></div></td>
                        <td>
                          <select className="input" value={tracks.find((t) => t.input?.kind === 'bridge' && t.input.channel === c.channel)?.id ?? ''}
                            onChange={(e) => commit('Eingang zuweisen', (p) => {
                              p.tracks.forEach((t) => { if (t.input?.kind === 'bridge' && t.input.channel === c.channel) t.input = { kind: 'browser' }; });
                              const t = p.tracks.find((x) => x.id === e.target.value);
                              if (t) { t.input = { kind: 'bridge', channel: c.channel, label: c.name }; toast(`${c.name} → ${t.name}`); }
                            })}>
                            <option value="">–</option>
                            {tracks.filter((t) => t.kind === 'audio').map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          <details className="sigpath">
            <summary>Signalweg bei Dante</summary>
            <div className="stack" style={{ marginTop: 8 }}>
              {['Dante-Geräte im Studio (z. B. A32 Pro, RedNet, zweites Studio per Dante Connect)', 'Dante Controller: Routing auf die Kanäle des Digiface Dante bzw. der Dante Virtual Soundcard', 'Core Audio (Mac) bzw. ASIO (Windows)', 'SRS Bridge: Pegel, verlustfreie Aufnahme, Latenzausgleich, eure Plugins', 'Upload als Take, alle im Raum hören ihn'].map((t, i) => (
                <div key={i} className="card row"><span className="pill blue">{i + 1}</span><span>{t}</span></div>
              ))}
              <div className="t2" style={{ fontSize: 12 }}>Routing direkt aus der DAW (ohne Dante Controller) braucht eine Lizenz der Audinate-API. Bis dahin bleibt das Routing im Dante Controller.</div>
            </div>
          </details>
        </div>
      </Panel>
    </div>
  );
}
